import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { User, MapPin, ArrowRight, Loader2 } from 'lucide-react'
import useAuthStore from '@/stores/authStore'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/config/supabase'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import { ROLE_HOME } from '@/config/routes'

const schema = z.object({
  full_name: z.string().min(2, 'Full name must be at least 2 characters').max(80),
  phone: z.string().optional().default(''),
  city: z.string().min(2, 'Please enter your city').max(60),
})

export default function SetupProfile() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const fetchProfile = useAuthStore((s) => s.fetchProfile)
  const [saving, setSaving] = useState(false)
  const [serverError, setServerError] = useState(null)
  // true while we check if the profile already exists — show spinner, not form
  const [checking, setChecking] = useState(true)

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      full_name: user?.user_metadata?.full_name ?? user?.user_metadata?.name ?? '',
      phone: '',
      city: '',
    },
  })

  // ── On mount: check DB directly for an existing complete profile ───────────
  // If one exists, skip the form and route immediately to the correct dashboard.
  // This handles existing users (admin/worker/customer) who land here because
  // fetchProfile returned null during a timing window right after OAuth.
  useEffect(() => {
    async function checkExistingProfile() {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user?.id) { setChecking(false); return }

      const { data: existing } = await supabase
        .from('profiles')
        .select('role, full_name, city')
        .eq('id', session.user.id)
        .single()

      if (existing?.full_name && existing?.city) {
        // Profile is complete — update store and route to correct home
        await fetchProfile(session.user.id)
        navigate(ROLE_HOME[existing.role] ?? ROLE_HOME.customer, { replace: true })
        return
      }

      setChecking(false)
    }

    checkExistingProfile()
  }, []) // eslint-disable-line

  const onSubmit = async (data) => {
    if (saving) return
    setSaving(true)
    setServerError(null)

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user) {
        setServerError('Session expired. Please sign in again.')
        setSaving(false)
        return
      }

      const userId    = session.user.id
      const userEmail = session.user.email ?? null

      const { error } = await supabase.from('profiles').upsert({
        id:         userId,
        full_name:  data.full_name.trim(),
        phone:      data.phone?.trim() ?? '',
        city:       data.city.trim(),
        email:      userEmail,
        updated_at: new Date().toISOString(),
        // role intentionally omitted — never overwrite an existing role from the client
      }, { onConflict: 'id', ignoreDuplicates: false })

      if (error) {
        setServerError(`Could not save: ${error.message}`)
        setSaving(false)
        return
      }

      // Read role directly from DB — do not rely on store which may lag
      const { data: saved } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', userId)
        .single()

      const role = saved?.role ?? 'customer'
      await fetchProfile(userId)
      navigate(ROLE_HOME[role] ?? ROLE_HOME.customer, { replace: true })

    } catch (err) {
      if (err?.name === 'AbortError' || err?.message?.includes('Lock broken')) {
        console.warn('Lock contention detected, retrying in 500ms…')
        await new Promise((r) => setTimeout(r, 500))
        try {
          const { data: { session } } = await supabase.auth.getSession()
          if (!session?.user) {
            setServerError('Session expired. Please sign in again.')
            setSaving(false)
            return
          }

          const { error: retryError } = await supabase.from('profiles').upsert({
            id:         session.user.id,
            full_name:  data.full_name.trim(),
            phone:      data.phone?.trim() ?? '',
            city:       data.city.trim(),
            email:      session.user.email ?? null,
            updated_at: new Date().toISOString(),
            // role intentionally omitted
          }, { onConflict: 'id', ignoreDuplicates: false })

          if (retryError) {
            setServerError(`Could not save: ${retryError.message}`)
            setSaving(false)
            return
          }

          const { data: saved } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', session.user.id)
            .single()

          const role = saved?.role ?? 'customer'
          await fetchProfile(session.user.id)
          navigate(ROLE_HOME[role] ?? ROLE_HOME.customer, { replace: true })
          return
        } catch (retryErr) {
          setServerError('Still having trouble saving. Please refresh and try again.')
          setSaving(false)
          return
        }
      }

      setServerError(err.message)
      setSaving(false)
    }
  }

  // Show spinner while checking for existing profile
  if (checking) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-surface-base">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 bg-siglat-teal/15 rounded-lg flex items-center justify-center">
            <span className="text-siglat-teal text-sm font-black">SK</span>
          </div>
          <Loader2 className="w-5 h-5 text-siglat-teal animate-spin" />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-dvh bg-surface-base flex flex-col items-center justify-center px-4 py-12">
      <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden>
        <div className="absolute -top-32 left-0 w-80 h-80 bg-siglat-teal/5 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-sm flex flex-col gap-8 animate-fade-in">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="w-14 h-14 bg-siglat-teal rounded-xl flex items-center justify-center shadow-teal-glow">
            <span className="text-surface-base text-lg font-black">SK</span>
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-ink-primary">Complete your profile</h1>
            <p className="text-sm text-ink-muted mt-1">Just a few details to get you started.</p>
          </div>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="bg-surface-raised border border-surface-border rounded-xl p-6 shadow-card flex flex-col gap-5"
          noValidate
        >
          <Input
            label="Full name"
            placeholder="Juan dela Cruz"
            leftElement={<User className="w-4 h-4" />}
            error={errors.full_name?.message}
            autoComplete="name"
            {...register('full_name')}
          />

          <Input
            label="Mobile number (optional)"
            placeholder="09171234567"
            leftElement={<span className="text-sm">🇵🇭</span>}
            error={errors.phone?.message}
            hint="You can add this later"
            autoComplete="tel"
            type="tel"
            {...register('phone')}
          />

          <Input
            label="City / Municipality"
            placeholder="Quezon City"
            leftElement={<MapPin className="w-4 h-4" />}
            error={errors.city?.message}
            hint="Used to match you with nearby workers"
            autoComplete="address-level2"
            {...register('city')}
          />

          {serverError && (
            <div className="bg-siglat-red/10 border border-siglat-red/25 rounded-md p-3">
              <p className="text-xs text-siglat-red flex items-center gap-1.5">
                <span>⚠</span> {serverError}
              </p>
            </div>
          )}

          <Button
            type="submit"
            size="lg"
            fullWidth
            loading={saving}
            rightIcon={<ArrowRight className="w-4 h-4" />}
          >
            Let's go
          </Button>
        </form>

        <p className="text-xs text-ink-muted text-center px-4">
          Your information is only used for booking coordination and is never sold.
        </p>
      </div>
    </div>
  )
}
